<script lang="ts" setup>
import type { LogicalFilterAND, PrimitiveFieldPath } from '#layers/data-model/public';
import type { FilterSchemaCollection } from './utils/filter-schema';
import { ref } from 'vue';
import { DisplayTemplateConfigurationError, isEmptyFilter, isFieldFilter, isLogicalFilter } from '#layers/data-model/public';
import { useLogicalFilter } from '#layers/filters/composables/use-logical-filter';
import { injectFilterSchemaContext } from './utils/filter-schema-context';
import { useFilterConfigResolver } from './utils/use-filter-config-resolver';

withDefaults(defineProps<{
	collection: FilterSchemaCollection;
	autoAdvanceIndex?: number | null;
}>(), {
	autoAdvanceIndex: null,
});

const emit = defineEmits<{
	autoAdvanceHandled: [];
}>();

const filter = defineModel<LogicalFilterAND>({ default: () => ({ _and: [] }) });

const { findPrimitiveFieldById, findSingleRelationFieldById } = injectFilterSchemaContext();
const { supportedOperatorsFor, defaultOperatorFor } = useFilterConfigResolver();

const { handleRemove, handleAdd, handleUpdate, handleGroupAdd } = useLogicalFilter(filter, {
	addGroupToFront: true,
	findPrimitiveFieldById,
	findSingleRelationFieldById,
	supportedOperatorsFor,
	defaultOperatorFor,
});

// A clause added from the "+" picker auto-advances too, mirroring the column-header action:
// capture the inserted index and drive the same in-FilterSimple operator→value step machine.
const manualAutoAdvanceIndex = ref<number | null>(null);

function onAddFilter(fieldPath: PrimitiveFieldPath) {
	const index = handleAdd(fieldPath);
	if (index !== undefined) manualAutoAdvanceIndex.value = index;
}

function onAutoAdvanceDone() {
	manualAutoAdvanceIndex.value = null;
	emit('autoAdvanceHandled');
}
</script>

<template>
	<NuxtErrorBoundary>
		<template #error>
			<span class="flex items-center gap-2">
				<DisplayTemplateConfigurationError :error="{ message: $t('filters.invalid_filter') }" />
				<DButton variant="ghost" color="neutral" size="xs" @click="filter = { _and: [] }">
					{{ $t('filters.clear_filter') }}
				</DButton>
			</span>
		</template>

		<div class="flex flex-1 shrink-0 items-center gap-2 overflow-hidden" :class="{ '-ms-4.5': isEmptyFilter(filter) }">
			<div class="scrollbar-hidden flex items-center gap-2 overflow-x-auto">
				<template
					v-for="(subFilter, index) in filter._and"
					:key="index"
				>
					<FilterAdvanced
						v-if="isLogicalFilter(subFilter)"
						:model-value="subFilter"
						:collection
						@update:model-value="handleUpdate(index, $event)"
						@remove="handleRemove(index)"
					/>
					<FilterSimple
						v-else-if="isFieldFilter(subFilter)"
						:model-value="subFilter"
						:collection
						:auto-advance="autoAdvanceIndex === index || manualAutoAdvanceIndex === index"
						@update:model-value="handleUpdate(index, $event)"
						@remove="handleRemove(index)"
						@auto-advance-done="onAutoAdvanceDone"
					/>
				</template>
			</div>
			<FilterAdd
				:collection
				@add-filter="onAddFilter"
				@add-group="handleGroupAdd"
			/>
		</div>
	</NuxtErrorBoundary>
</template>
